import { Loader2, AlertCircle, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { useWebhookEventDetails } from '../../hooks/useWebhookLogs';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface WebhookEventDetailsProps {
  eventId: string | undefined;
  isOpen: boolean;
  onClose: () => void;
}

export function WebhookEventDetails({
  eventId,
  isOpen,
  onClose,
}: WebhookEventDetailsProps) {
  const { data, isLoading, error } = useWebhookEventDetails(eventId);
  const [copied, setCopied] = useState(false);

  const handleCopyPayload = () => {
    if (data?.payload) {
      navigator.clipboard.writeText(JSON.stringify(data.payload, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Detalhes do Webhook</SheetTitle>
          <SheetDescription>Informações completas do evento</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Error state */}
          {!!error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Erro ao carregar detalhes do webhook
              </AlertDescription>
            </Alert>
          )}

          {/* Event details */}
          {data && (
            <>
              {/* Basic info */}
              <Card>
                <CardHeader>
                  <CardTitle>Informações Básicas</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">
                        Tipo
                      </div>
                      <div className="text-base">{data.webhookType}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">
                        Provider
                      </div>
                      <div className="text-base">{data.provider}</div>
                    </div>
                  </div>

                  {data.eventName && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">
                        Nome do Evento
                      </div>
                      <div className="text-base">{data.eventName}</div>
                    </div>
                  )}

                  {data.senderId && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">
                        Remetente
                      </div>
                      <div className="text-base">{data.senderId}</div>
                    </div>
                  )}

                  <div>
                    <div className="text-sm font-medium text-muted-foreground">
                      Status
                    </div>
                    <Badge variant="default" className="mt-1">
                      {data.status}
                    </Badge>
                  </div>

                  {data.errorMessage && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">
                        Erro
                      </div>
                      <div className="text-sm text-destructive">
                        {data.errorMessage}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">
                        Criado em
                      </div>
                      <div className="text-sm">
                        {format(
                          new Date(data.createdAt),
                          "dd/MM/yyyy 'às' HH:mm:ss",
                          { locale: ptBR },
                        )}
                      </div>
                    </div>
                    {data.processedAt && (
                      <div>
                        <div className="text-sm font-medium text-muted-foreground">
                          Processado em
                        </div>
                        <div className="text-sm">
                          {format(
                            new Date(data.processedAt),
                            "dd/MM/yyyy 'às' HH:mm:ss",
                            { locale: ptBR },
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Payload */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Payload JSON</CardTitle>
                      <CardDescription>Dados completos do webhook</CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyPayload}
                    >
                      {copied ? (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Copiado!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-2" />
                          Copiar
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <pre className="text-xs bg-muted p-4 rounded-lg overflow-x-auto">
                    {JSON.stringify(data.payload, null, 2)}
                  </pre>
                </CardContent>
              </Card>

              {/* Metadata (if exists) */}
              {data.metadata && (
                <Card>
                  <CardHeader>
                    <CardTitle>Metadados</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="text-xs bg-muted p-4 rounded-lg overflow-x-auto">
                      {JSON.stringify(data.metadata, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
              )}

              {/* IDs */}
              <Card>
                <CardHeader>
                  <CardTitle>Identificadores</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <div className="text-xs font-medium text-muted-foreground">
                      Event ID
                    </div>
                    <div className="text-sm font-mono">{data.id}</div>
                  </div>
                  {data.webhookId && (
                    <div>
                      <div className="text-xs font-medium text-muted-foreground">
                        Webhook ID
                      </div>
                      <div className="text-sm font-mono">{data.webhookId}</div>
                    </div>
                  )}
                  {data.channelConnectionId && (
                    <div>
                      <div className="text-xs font-medium text-muted-foreground">
                        Channel Connection ID
                      </div>
                      <div className="text-sm font-mono">
                        {data.channelConnectionId}
                      </div>
                    </div>
                  )}
                  {data.assistantId && (
                    <div>
                      <div className="text-xs font-medium text-muted-foreground">
                        Assistant ID
                      </div>
                      <div className="text-sm font-mono">{data.assistantId}</div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
